import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Card, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Category } from '../../types/category';
import { categoryService } from '../../services/categoryService';
import CategoryForm from './CategoryForm';

const { Title } = Typography;

const CategoriesPage: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(undefined);

  // Function to fetch all categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await categoryService.getAllCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error(t('categories.notifications.error', { message: (error as Error).message }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Handle editing a category
  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormVisible(true);
  };

  // Handle creating a new category
  const handleCreate = () => {
    setSelectedCategory(undefined);
    setFormVisible(true);
  };

  // Handle deleting a category
  const handleDelete = async (id: number) => {
    try {
      await categoryService.deleteCategory(id);
      message.success(t('categories.notifications.delete_success'));
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error(t('categories.notifications.error', { message: (error as Error).message }));
    }
  };

  // After successful form submission, refresh the list
  const handleFormSuccess = () => {
    fetchCategories();
  };

  // Table columns
  const columns = [
    {
      title: t('categories.list.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('categories.list.secondary_name'),
      dataIndex: 'secondary_name',
      key: 'secondary_name',
      render: (text: string | null) => text || '-'
    },
    {
      title: t('categories.list.images'),
      key: 'images',
      width: 120,
      render: (_: any, record: Category) => {
        const hasHorizontal = record.image_horizontal && record.image_horizontal !== '';
        const hasVertical = record.image_vertical && record.image_vertical !== '';
        
        if (!hasHorizontal && !hasVertical) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        
        return (
          <Space size={4}>
            {hasHorizontal && (
              <Tooltip title={t('categories.list.has_horizontal_image', 'Has horizontal image')}>
                <Tag color="blue" icon={<PictureOutlined />} style={{ margin: 0 }}>
                  H
                </Tag>
              </Tooltip>
            )}
            {hasVertical && (
              <Tooltip title={t('categories.list.has_vertical_image', 'Has vertical image')}>
                <Tag color="green" icon={<PictureOutlined />} style={{ margin: 0 }}>
                  V
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: t('categories.list.created'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('categories.list.updated'),
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString()
    },
    {
      title: t('categories.list.actions'),
      key: 'actions',
      render: (_: any, record: Category) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            {t('categories.actions.edit')}
          </Button>
          <Popconfirm
            title={t('categories.actions.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              {t('categories.actions.delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title={t('categories.list.title')}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div></div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          {t('categories.actions.create')}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={categories}
        rowKey="id"
        loading={loading}
      />

      <CategoryForm
        category={selectedCategory}
        isVisible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={handleFormSuccess}
      />
    </Card>
  );
};

export default CategoriesPage; 